import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { CreditCard, Loader2, Search, Sparkles, Crown, Save, CheckCircle2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { GenesisRosterCard } from '../components/GenesisRosterCard';

const planIcon = (plan) => (plan === 'genesis_premium' ? Crown : plan === 'ministerial' ? Wallet : Sparkles);
const planColor = (plan) => (plan === 'genesis_premium' ? '#C58B2A' : plan === 'ministerial' ? '#2B7DBA' : '#056A84');

export default function SubscriptionsPage() {
  const { isSuperAdmin, selectedChurchId } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [catalog, setCatalog] = useState(null);
  const [churches, setChurches] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [plan, setPlan] = useState('genesis');
  const [addons, setAddons] = useState([]);
  const [saving, setSaving] = useState(false);
  const pg = usePagination(search.trim().toLowerCase());

  const fetchData = useCallback(() => {
    setLoading(true);
    const requests = [api.get('/api/subscriptions/plans')];
    requests.push(isSuperAdmin
      ? api.get('/api/subscriptions')
      : api.get(`/api/subscriptions/church/${selectedChurchId}`));

    Promise.all(requests)
      .then(([catRes, dataRes]) => {
        setCatalog(catRes.data);
        if (isSuperAdmin) setChurches(dataRes.data?.data || []);
        else setMine(dataRes.data);
      })
      .catch(err => toast.error(hubError(err, 'Error al cargar los planes')))
      .finally(() => setLoading(false));
  }, [isSuperAdmin, selectedChurchId]);

  useEffect(() => {
    if (isSuperAdmin || selectedChurchId) fetchData();
    else setLoading(false);
  }, [fetchData, isSuperAdmin, selectedChurchId]);

  const openEdit = (church) => {
    setEditing(church);
    setPlan(church.plan);
    setAddons(church.addons || []);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/subscriptions/church/${editing.id}`, { plan, addons });
      toast.success('Plan actualizado');
      setEditing(null);
      fetchData();
    } catch (err) { toast.error(hubError(err, 'Error al actualizar el plan')); } finally { setSaving(false); }
  };

  const addonLabel = (key) => catalog?.addons?.find(a => a.key === key)?.label || key;

  if (loading) {
    return (
      <div>
        <TopBar title="Suscripciones" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
      </div>
    );
  }

  // ===== Vista del pastor: su plan =====
  if (!isSuperAdmin) {
    const PlanIcon = planIcon(mine?.plan);
    return (
      <div>
        <TopBar title="Suscripción" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
        <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-2xl">
          <Card className="stat-card" data-testid="my-subscription-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${planColor(mine?.plan)}15` }}>
                  <PlanIcon className="w-6 h-6" style={{ color: planColor(mine?.plan) }} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold" data-testid="my-plan-label">{mine?.plan_label}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{mine?.plan_description}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {mine?.pays_base ? (
                      <Badge className="bg-[#2B7DBA]/15 text-[#2B7DBA] hover:bg-[#2B7DBA]/15 text-[11px]" data-testid="my-plan-price">
                        {mine?.price?.monthly} {mine?.price?.currency}/mes · {mine?.price?.detail}
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[11px]">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Suscripción base sin costo de por vida
                      </Badge>
                    )}
                    {mine?.pays_addons === false && (
                      <Badge className="bg-[#C58B2A]/15 text-[#C58B2A] hover:bg-[#C58B2A]/15 text-[11px]">
                        Módulos adicionales incluidos
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t mt-6 pt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Módulos adicionales
                </p>
                {mine?.pays_addons === false ? (
                  <p className="text-sm text-muted-foreground">
                    Tu plan incluye todos los módulos que se agreguen a la Suite, sin pagos extra.
                  </p>
                ) : (mine?.addons || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Aún no tienes módulos adicionales activos.
                    {mine?.pays_base
                      ? ' Se suman a la mensualidad de tu plan.'
                      : ' La suscripción base no tiene costo; solo se cobran los módulos nuevos que decidas activar.'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="my-addons">
                    {mine.addons.map(a => <Badge key={a} variant="secondary" className="text-xs">{addonLabel(a)}</Badge>)}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ===== Vista del administrador global =====
  const term = search.trim().toLowerCase();
  const filtered = term ? churches.filter(c => (c.name || '').toLowerCase().includes(term)) : churches;
  const visible = filtered.slice(pg.offset, pg.offset + pg.pageSize);
  const monthlyTotal = churches.reduce((sum, c) => sum + (c.price?.monthly || 0), 0);

  return (
    <div>
      <TopBar title="Suscripciones" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-2xl font-semibold text-[#056A84]" data-testid="genesis-used">{catalog?.genesis_used ?? 0}</p>
              <p className="text-xs text-muted-foreground">Génesis registradas</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-2xl font-semibold text-[#1F9D7A]" data-testid="genesis-available">{catalog?.genesis_available ?? 0}</p>
              <p className="text-xs text-muted-foreground">Del padrón, por registrarse (de {catalog?.genesis_seats ?? 0})</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-2xl font-semibold text-[#2B7DBA]" data-testid="paid-count">
                {churches.filter(c => c.plan === 'ministerial').length}
              </p>
              <p className="text-xs text-muted-foreground">
                Plan Ministerial (pago)
                {monthlyTotal > 0 && ` · ${monthlyTotal.toFixed(2)} USD/mes`}
              </p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-2xl font-semibold text-[#C58B2A]" data-testid="premium-count">
                {churches.filter(c => c.plan === 'genesis_premium').length}
              </p>
              <p className="text-xs text-muted-foreground">Génesis Premium</p>
            </CardContent>
          </Card>
        </div>

        <div className="mb-6">
          <GenesisRosterCard onImported={fetchData} />
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar congregación..." className="pl-9" data-testid="subscriptions-search-input" />
        </div>

        <Pagination
          total={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="congregaciones" testId="subscriptions-pagination"
        />

        {filtered.length === 0 ? (
          <div className="empty-state"><CreditCard className="w-12 h-12 text-muted-foreground/40" /><p>No hay congregaciones</p></div>
        ) : (
          <div className="space-y-3">
            {visible.map(c => {
              const PlanIcon = planIcon(c.plan);
              return (
                <Card key={c.id} className="stat-card" data-testid={`subscription-row-${c.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${planColor(c.plan)}15` }}>
                        <PlanIcon className="w-5 h-5" style={{ color: planColor(c.plan) }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.plan_label}
                          {c.plan === 'ministerial' && (
                            <span data-testid={`subscription-price-${c.id}`}>
                              {' · '}{(c.price?.monthly ?? 0).toFixed(2)} USD/mes
                              {typeof c.members === 'number' && ` · ${c.members} miembros aprobados`}
                            </span>
                          )}
                          {c.in_genesis_roster && <span className="text-[#1F9D7A]"> · en el padrón</span>}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {c.pays_addons === false ? (
                            <Badge className="bg-[#C58B2A]/15 text-[#C58B2A] hover:bg-[#C58B2A]/15 text-[10px]">Todo incluido</Badge>
                          ) : (c.addons || []).length === 0 ? (
                            <span className="text-[10px] text-muted-foreground">Sin módulos adicionales</span>
                          ) : c.addons.map(a => (
                            <Badge key={a} variant="secondary" className="text-[10px]">{addonLabel(a)}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`subscription-edit-${c.id}`}>
                      Gestionar plan
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Plan de {editing?.name}</DialogTitle>
            <DialogDescription>
              No existe un plan gratuito: el Plan Génesis cubre la suscripción base de por vida
              para las {catalog?.genesis_seats ?? 0} congregaciones del padrón fundador; el resto
              entra al Plan Ministerial de pago.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Plan</label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="subscription-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catalog?.plans || []).map(p => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {(catalog?.plans || []).find(p => p.key === plan)?.description}
              </p>
            </div>

            {plan !== 'genesis_premium' && (              <div className="space-y-2">
                <label className="text-sm font-medium">Módulos adicionales contratados</label>
                <div className="space-y-2">
                  {(catalog?.addons || []).map(a => (
                    <label key={a.key} className={`flex items-center gap-3 p-2 rounded-lg border ${a.included_in_all ? 'opacity-70' : 'cursor-pointer hover:bg-accent/40'}`}>
                      <Checkbox
                        checked={a.included_in_all || addons.includes(a.key)}
                        disabled={a.included_in_all}
                        onCheckedChange={() => setAddons(prev => prev.includes(a.key) ? prev.filter(x => x !== a.key) : [...prev, a.key])}
                        data-testid={`subscription-addon-${a.key}`}
                      />
                      <span className="text-sm">{a.label}</span>
                      {a.included_in_all && (
                        <Badge variant="secondary" className="text-[10px] ml-auto">Incluido en todos los planes</Badge>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="subscription-save-button">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
